<template>
    <b-modal id="discount-modal" dialog-class="base-modal" body-class="px-md-4 pb-4"
        footer-class="justify-content-center" @show="initialize" centered>
        <template #modal-title>
            <span class="fw-bold">Apply a discount</span>
        </template>
        <div class="mb-2">
            <TagIcon :fill="webDesign.design.main_color" />
            <span class="fw-bold ml-2">Promo Code</span>
        </div>
        <div class="d-flex">
            <b-input-group class="mr-3">
                <b-input v-model="promoCode" placeholder="Enter Code" id="promo-code"
                    :class="{ 'border-right-0': promoCode }" :disabled="!!cartPromoCode" autofocus />
                <template #append>
                    <b-input-group-text v-if="promoCode" class="bg-white cursor-pointer" @click="removePromoCode">
                        <ClearIcon />
                    </b-input-group-text>
                </template>
            </b-input-group>
            <b-button pill variant="primary" class="px-4 px-md-5" @click="applyPromoCode" 
                :style="{
                    'background-color': webDesign && webDesign.design ? `${webDesign.design.main_color} !important` : null,
                    border: webDesign && webDesign.design ? 'none !important' : null,
                    color: webDesign && webDesign.design ? `${webDesign.design.button_text_color} !important` : null
                    }"
            >
                {{ cartPromoCode ? "Applied" : "Apply" }}
            </b-button>
        </div>
        <div v-if="!!promoCodeError" class="invalid-feedback d-block">
            {{ promoCodeError }}
        </div>
        <div id="view-available-promo-codes" class="fw-bold cursor-pointer"
            :style="{ backgroundColor: hexToRgba(webDesign.design.main_color, 0.2) }" @click="viewAvailablePromoCodes">
            <div> Or view <span :style="{ color: webDesign.design.main_color }">available vouchers</span></div>
            <ChevronRightIcon :fill="webDesign.design.main_color" />
        </div>
        <template v-if="isSpecialDiscountEnabled && hasCartSpecialDiscountItem && isSingleBranchCart">
            <div id="special-discount">
                <IdCard :fill="webDesign.design.main_color" />
                <span class="fw-bold ml-2 align-middle">Senior Citizen / PWD ID</span>
            </div>
            <JumpingDotsLoader v-if="loading" :fill="webDesign.design.main_color" />
            <template v-else>
                <b-container class="saved-special-discount"
                    :class="{ 'bg-light border-0': !enabledSpecialDiscountTypes.includes(specialDiscount.discount_type) }"
                    v-for="(specialDiscount, index) in specialDiscountList" :key="index"
                    :style="{ ...(isSelectedSpecialDiscount(specialDiscount) && { borderColor: webDesign.design.main_color }) }">
                    <b-row>
                        <b-col>
                            <div class="d-flex justify-content-between align-items-center">
                                <div v-if="enabledSpecialDiscountTypes.includes(specialDiscount.discount_type)"
                                    @click="selectedSpecialDiscount = isSelectedSpecialDiscount(specialDiscount) ? null : specialDiscount"
                                    class="check-circle cursor-pointer"
                                    :class="{ 'checked': isSelectedSpecialDiscount(specialDiscount) }"
                                    :style="{ ...(isSelectedSpecialDiscount(specialDiscount) && { outlineColor: webDesign.design.main_color }) }">
                                    <CheckCircleIcon v-if="isSelectedSpecialDiscount(specialDiscount)"
                                        :fill="webDesign.design.main_color" />
                                </div>
                                <div class="saved-special-discount-info">
                                    <div class="heading fw-bold text-truncate"
                                        :class="{ 'text-muted': !enabledSpecialDiscountTypes.includes(specialDiscount.discount_type) }">
                                        {{ specialDiscountLabel[specialDiscount.discount_type] }}
                                    </div>
                                    <div class="details text-truncate"
                                        :class="{ 'text-muted': !enabledSpecialDiscountTypes.includes(specialDiscount.discount_type) }">
                                        {{ specialDiscount.customer_id_full_name }}</div>
                                    <div class="details text-truncate"
                                        :class="{ 'text-muted': !enabledSpecialDiscountTypes.includes(specialDiscount.discount_type) }">
                                        {{ specialDiscount.customer_id_number }}</div>
                                    <div v-if="specialDiscount.discount_type !== 'pwd'" class="details"
                                        :class="{ 'text-muted': !enabledSpecialDiscountTypes.includes(specialDiscount.discount_type) }">
                                        {{ idLabel[specialDiscount.customer_id_type] }}
                                    </div>
                                    <div v-if="!enabledSpecialDiscountTypes.includes(specialDiscount.discount_type)"
                                        class="details"
                                        :class="{ 'text-muted': !enabledSpecialDiscountTypes.includes(specialDiscount.discount_type) }">
                                        <i class="bi bi-exclamation-circle-fill text-danger" aria-hidden="true"></i>
                                        <span class="ml-1 text-danger">Invalid ID</span>
                                    </div>
                                </div>
                            </div>
                        </b-col>
                        <b-col class="d-flex justify-content-end align-items-center" cols="2" md="1">
                            <b-button class="delete-special-discount-trash bg-transparent p-0 border-0 shadow-none"
                                @click="deleteSpecialDiscount(specialDiscount.uuid)">
                                <TrashIcon class="cursor-pointer" />
                            </b-button>
                        </b-col>
                    </b-row>
                </b-container>
                <div v-if="specialDiscountList.length < 2" class="register-new-id fw-bold cursor-pointer"
                    :class="{ 'register-new-id-initial': !specialDiscountList.length }"
                    :style="{ borderColor: webDesign.design.main_color, color: webDesign.design.main_color }"
                    @click="registerNewId">
                    <PlusCircleIcon :fill="webDesign.design.main_color" />
                    <span class="ml-2">Register new ID</span>
                </div>
            </template>
        </template>
        <template #modal-footer>
            <b-button pill variant="primary" id="confirm-button" class="w-75"
                @click="confirmSelectedSpecialDiscount"
                :style="{
                  'background-color': webDesign && webDesign.design ? `${webDesign.design.main_color} !important` : null,
                  border: webDesign && webDesign.design ? 'none !important' : null,
                  color: webDesign && webDesign.design ? `${webDesign.design.button_text_color} !important` : null
                }">
                Confirm
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { hexToRgba } from "@/utilities";
import _ from "lodash";
import TagIcon from "@/components/icons/TagIcon";
import ClearIcon from "@/components/icons/ClearIcon";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon";
import IdCard from "@/components/icons/IdCard";
import TrashIcon from "@/components/icons/TrashIcon"
import PlusCircleIcon from "@/components/icons/PlusCircleIcon";
import CheckCircleIcon from "@/components/icons/CheckCircleIcon";
import JumpingDotsLoader from "@/components/common/JumpingDotsLoader";

export default {
    data() {
        return {
            hexToRgba,
            promoCode: "",
            promoCodeError: "",
            specialDiscountLabel: {
                senior: "Senior Citizen",
                pwd: "PWD"
            },
            idLabel: {
                "PASSPORT": "Passport",
                "SSS": "SSS",
                "NBI": "NBI Clearance",
                "GSIS_SSS": "GSIS or SSS Umid Card",
                "IBP_ID": "IBP ID",
                "PRC": "PRC ID",
                "TDCC": "Theoretical Diving Course Certificate",
                "POLICECLR": "Police Clearance",
                "BIRTHCERT": "Birth Certificate",
                "SCHOOL_ID": "School ID",
                "SENIOR": "Senior Citizen ID",
                "OWWA": "OWWA ID",
                "POSTAL_ID": "Postal ID",
                "GSR": "Government Service Record",
                "BRGYCERT": "Barangay Certification"
            },
            selectedSpecialDiscount: null,
        }
    },
    computed: {
        ...mapState("estore", ["webDesign", "voucher", "loading_voucher", "cart", "cartSpecialDiscount"]),
        ...mapState("auth", ["token"]),
        ...mapState("merchant", ["customerPortalToken"]),
        ...mapState("customer", ["user", "specialDiscountList", "loading"]),
        ...mapGetters("estore", ["isSpecialDiscountEnabled", "hasCartSpecialDiscountItem", "isSingleBranchCart", "cartUuid", "specialDiscountSettings"]),
        cartPromoCode() {
            return _.get(this.cart, "voucher_info.code", "");
        },
        requestHeader() {
            return {
                Authorization: this.token,
                "X-Merchant-Token": this.customerPortalToken
            };
        },
        enabledSpecialDiscountTypes() {
            return _.get(this.specialDiscountSettings, "discount_types", []);
        }
    },
    components: {
        TagIcon,
        ClearIcon,
        ChevronRightIcon,
        IdCard,
        TrashIcon,
        PlusCircleIcon,
        CheckCircleIcon,
        JumpingDotsLoader
    },
    methods: {
        async removePromoCode() {
            if (this.cartPromoCode && !this.loading_voucher) {
                await this.$store.dispatch("estore/removeVoucher", {
                    header: this.requestHeader
                });
            }

            this.promoCode = "";
        },
        viewAvailablePromoCodes() {
            this.$bvModal.show("view-available-codes-modal");
            this.$bvModal.hide("discount-modal");
        },
        registerNewId() {
            this.$bvModal.show("upload-special-discount-id-modal");
            this.$bvModal.hide("discount-modal");
        },
        async applyPromoCode() {
            this.promoCodeError = "";

            if (this.promoCode) {
                if (!this.loading_voucher) {
                    const request = {
                        header: this.requestHeader,
                        payload: { voucher_code: this.promoCode }
                    };

                    await this.$store.dispatch("estore/verifyVoucher", request);

                    if (this.voucher && this.voucher.valid) {
                        this.$store.commit("estore/setCartSpecialDiscount", null);
                        await this.$store.dispatch("estore/applyVoucher", request);
                        this.$bvModal.hide("discount-modal");
                    } else this.promoCodeError = "Promo Code is invalid.";
                }
            } else this.promoCodeError = "Please enter promo code."
        },
        isSelectedSpecialDiscount(value) {
            return this.selectedSpecialDiscount &&
                this.selectedSpecialDiscount.discount_type === value.discount_type;
        },
        async initialize() {
            if (this.cartSpecialDiscount)
                this.selectedSpecialDiscount = _.cloneDeep(this.cartSpecialDiscount);
            else
                this.selectedSpecialDiscount = null;

            if (this.cartPromoCode)
                this.promoCode = this.cartPromoCode;

            await this.$store.dispatch(
                "customer/getSpecialDiscountList",
                { header: this.requestHeader, uuid: this.user.uuid }
            );
        },
        async confirmSelectedSpecialDiscount() {
            if (!this.loading_voucher) {
                if (this.cartPromoCode)
                    await this.removePromoCode();

                if (!this.selectedSpecialDiscount && this.cartSpecialDiscount)
                    this.$store.commit("estore/setCartSpecialDiscount", null);

                if (this.selectedSpecialDiscount) {
                    await this.getStoreDetails();
                    this.$store.commit(
                        "estore/setCartSpecialDiscount",
                        { ...this.cartSpecialDiscount, ...this.selectedSpecialDiscount }
                    );
                    await this.applySpecialDiscountToCart();
                }

                this.$root.$emit("refreshCartItems");
                this.$bvModal.hide("discount-modal");
            }
        },
        deleteSpecialDiscount(uuid) {
            this.$store.state.estore.specialDiscountToDelete = uuid;
            this.$bvModal.hide("discount-modal");
            this.$bvModal.show("confirm-delete-special-discount-modal");
        },
        async getStoreDetails() {
            await this.$store.dispatch("estore/getStoreDetails", {
                header: this.requestHeader
            });
        },
        async applySpecialDiscountToCart() {
            await this.$store.dispatch("estore/applySpecialDiscountToCart", {
                header: this.requestHeader,
                payload: {
                    cart_uuid: this.cartUuid,
                    special_discount_types: this.selectedSpecialDiscount.discount_type,
                    special_discount_terms: this.specialDiscountSettings.discount_terms,
                    special_discount_terms_item: this.specialDiscountSettings.discount_terms_item,
                }
            });
            this.$root.$emit("applyCheckoutChanges");
        },
    }
};
</script>

<style lang="scss" scoped>
#discount-modal {
    .form-control {
        border-radius: 4px;

        &:focus {
            box-shadow: none;
            border-color: #ced4da;
        }
    }

    #view-available-promo-codes {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 18px;
        padding: 8px 12px;
        border-radius: 7px;
        font-size: 14px;
    }

    #special-discount {
        margin-top: 39px;
        margin-bottom: 16px;
    }

    .saved-special-discount {
        border: 2px solid #B9BDC7;
        border-radius: 10px;
        padding: 24px;
        margin-bottom: 8px;

        .heading {
            color: #383E49;
            font-size: 16px;
            margin-bottom: 10px;
        }

        .details {
            color: #383E49;
            font-size: 14px;
        }
    }

    .saved-special-discount-info {
        width: 90%;
    }

    .register-new-id {
        border-style: dashed;
        border-width: 2px;
        border-radius: 10px;
        height: 48px;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .register-new-id-initial {
        height: 106px;
    }

    #confirm-button {
        height: 50px;
    }

    .check-circle {
        border: 0.5px solid #989FAD;
        height: 24px;
        width: 24px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;

        &.checked {
            width: 21px;
            height: 21px;
            outline-style: solid;
            outline-width: 1px;
            outline-offset: 3px;
            border: 0;
        }
    }

    #promo-code:disabled {
        background-color: #FFFFFF;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    @media (max-width: 575.98px) {
        .saved-special-discount-info {
            width: 75%;
        }
    }

    .delete-special-discount-trash {
        height: inherit;
    }
}
</style>